/**
 * VoiceCallDetailPage
 *
 * Call detail view with Transcript, Recording, and Tool Activity tabs + metadata sidebar.
 * Uses getCallWithDetails which returns call + agentName + transcript + toolLogs in one query.
 */

import { useParams, useNavigate } from 'react-router-dom';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ require three levels up to reach convex/_generated/
import { useVoiceCallDetail } from '@/hooks/useVoiceCalls';
import { TranscriptView } from '@/components/voiceAgents/TranscriptView';
import { CallRecordingPlayer } from '@/components/voiceAgents/CallRecordingPlayer';
import { ToolActivityView } from '@/components/voiceAgents/ToolActivityView';
import { CallDetailSidebar } from '@/components/voiceAgents/CallDetailSidebar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';

export function VoiceCallDetailPage() {
  const navigate = useNavigate();
  const { callId } = useParams<{ callId: string }>();
  const typedCallId = callId as Id<'voiceCalls'>; // AS_JUSTIFIED: one-time Id<> cast from route params

  const { data, isLoading } = useVoiceCallDetail(typedCallId);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Call not found.</p>
        <Button variant="ghost" className="mt-3" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>
    );
  }

  const { call, agentName, transcript, toolLogs } = data;

  return (
    <div className="min-h-full py-8 px-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          title="Back to call history"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Call Detail</h1>
          <p className="text-sm text-muted-foreground">
            {call.callerNumber} — {new Date(call.startedAt).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Two-column layout: tabs + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Main content tabs */}
        <div className="border border-border rounded-md overflow-hidden bg-card">
          <Tabs defaultValue="transcript">
            <TabsList className="w-full grid grid-cols-3 rounded-none border-b">
              <TabsTrigger value="transcript">Transcript</TabsTrigger>
              <TabsTrigger value="recording">Recording</TabsTrigger>
              <TabsTrigger value="tools">Tool Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="transcript" className="m-0">
              <TranscriptView segments={transcript?.segments ?? []} />
            </TabsContent>

            <TabsContent value="recording" className="m-0">
              <CallRecordingPlayer recordingUrl={call.recordingUrl} />
            </TabsContent>

            <TabsContent value="tools" className="m-0">
              <ToolActivityView toolLogs={toolLogs} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <CallDetailSidebar call={call} agentName={agentName} />
      </div>
    </div>
  );
}
